import { Card, LevelSelect, Button } from '@breward/ui';
import { isScoringEnabled } from '@breward/utils';
import classNames from 'classnames';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScoresheetSchemaSection } from '../../../../types/scoresheetSchemas';
import ScoresheetCommentDrawer from '../ScoresheetCommentDrawer/ScoresheetCommentDrawer';
import ScoresheetSubsection from '../ScoresheetSubsection/ScoresheetSubsection';

interface ScoresheetSectionProps {
  scoresheet?: any;
  section?: ScoresheetSchemaSection;
  onChange?: () => void;
  hideHeader?: boolean;
}

export const ScoresheetSection = ({
  scoresheet,
  section,
  onChange,
  hideHeader,
}: ScoresheetSectionProps) => {
  const [collapsed, setCollapsed] = useState(hideHeader ? false : true);
  const [commentDrawerOpened, setCommentDrawerOpened] = useState(false);
  const [t] = useTranslation();

  const commentsCount = scoresheet?.comments?.data?.filter(
    (c: any) => c?.sectionKey === section?.key,
  )?.length;

  const hidePoints =
    scoresheet?.entry?.data?.contestsEdition?.data?.settings?.judging
      ?.hidePointsOnScoresheet;

  // Schemas evaluated without points have no section points to show
  const scoringEnabled = isScoringEnabled({
    schema: scoresheet?.scoresheetSchema?.data,
    edition: scoresheet?.entry?.data?.contestsEdition?.data,
  });

  return (
    <div className="my-8 space-y-4">
      <ScoresheetCommentDrawer
        comments={scoresheet?.comments?.data}
        section={section}
        opened={commentDrawerOpened}
        onChange={() => onChange?.()}
        onClose={() => {
          setCommentDrawerOpened(false);
          onChange?.();
        }}
      />
      {!hideHeader && (
        <div className="font-header text-primary-500 border-b-3 border-b-solid border-b-primary-500 grid grid-cols-12 gap-4 pb-4 text-2xl font-medium">
          <div className="col-span-10">{section?.label}</div>
          <div className="relative col-span-2 flex justify-end">
            <div
              onClick={() => setCollapsed(!collapsed)}
              className={classNames(
                collapsed ? 'icon-[ph--caret-down]' : 'icon-[ph--caret-up]',
                'text-content-500 mr-4 h-6 w-6 cursor-pointer',
              )}
            />
            <div
              onClick={() => setCommentDrawerOpened(true)}
              className="icon-[ph--chat-circle-dots] text-content-500 h-6 w-6 cursor-pointer"
            />
            {commentsCount > 0 && (
              <div className="font-header bg-error-500 pointer-events-none absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-xs text-white">
                {commentsCount}
              </div>
            )}
          </div>
        </div>
      )}
      <>
        {(!collapsed || hideHeader) && (
          <section
            className="transition-all duration-300"
          >
            {hideHeader && (
              <div className="flex justify-end mb-4">
                <button
                  type="button"
                  onClick={() => setCommentDrawerOpened(true)}
                  className="relative inline-flex items-center gap-2 rounded-2xl border border-white/25 bg-white/30 px-4 py-2 font-sans text-sm font-medium text-content-700 shadow-sm backdrop-blur-xl transition-colors hover:bg-white/45 dark:border-content-700/45 dark:bg-content-900/35 dark:text-content-300 dark:hover:bg-content-900/55"
                >
                  <div className="icon-[ph--chat-circle-dots] h-4 w-4 shrink-0" />
                  <span>{t('global:shared.comments', 'Comments')}</span>
                  {commentsCount > 0 && (
                    <span className="flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-error-500 px-1 font-header text-[10px] leading-none text-white">
                      {commentsCount}
                    </span>
                  )}
                </button>
              </div>
            )}
            {section?.subsections?.map((subsection) => {
              return (
                <ScoresheetSubsection
                  key={subsection?.key}
                  values={scoresheet?.values}
                  section={section}
                  subsection={subsection}
                />
              );
            })}
            {!hidePoints && scoringEnabled && (
              <Card
                title={section?.label || t('Pages:JudgeWeb.ScoresheetSection.PointsHeader', 'Section points')}
                actions={
                  <div className="text-primary-700 dark:text-primary-300 font-sans text-xl font-bold">
                    {section?.key ? scoresheet?.values?.[section.key]?.points || 0 : 0} / {section?.maxPoints}
                  </div>
                }
              >
                <div className="col-span-12 flex w-full gap-1 md:gap-1.5">
                  {Array.from({ length: section?.maxPoints || 0 }).map((_, i) => {
                    const points = section?.key ? scoresheet?.values?.[section.key]?.points || 0 : 0;
                    const isFilled = i < points;
                    return (
                      <div
                        key={i}
                        className={`h-4 flex-1 rounded-sm transition-colors ${
                          isFilled
                            ? 'bg-primary-500 dark:bg-primary-400 shadow-sm'
                            : 'bg-white/40 dark:bg-content-700/40 border border-content-200/50 dark:border-content-600/50'
                        }`}
                      />
                    );
                  })}
                </div>
              </Card>
            )}
          </section>
        )}
      </>
    </div>
  );
};

export default ScoresheetSection;
